@use '../../styles/mixins.scss' as mixins;
@use '../../styles/surface.scss' as surface;
@use '../../styles/border.scss' as border;
@use '../../styles/tag.scss' as tag;
@use '../../styles/typography.scss' as typography;

.chip {
  width: max-content;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 0 4px;
  gap: 2px;
  flex-shrink: 0;
  user-select: none;
  transition-property: transform, background-color, border-color;
  transition-duration: 100ms;
  transition-timing-function: ease-out;

  // Reset button defaults when rendered as <button> (interactive Chip)
  font: inherit;
  color: inherit;
  text-align: inherit;
  appearance: none;
  border: none;

  &:hover:not(.disabled) {
    transition-duration: 150ms;
    transition-timing-function: ease;
  }

  &:active:not(.disabled) {
    transform: scale(0.97);
  }

  max-width: 100%;

  .content {
    min-width: 0;
  }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .leadingIcon,
  .trailingIcon,
  .deleteButton {
    flex-shrink: 0;
  }
}

// Interactive (clickable) Chip — rendered as <button>
.interactive {
  cursor: pointer;

  &:focus-visible {
    @include mixins.focus-ring;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

// Sizes
.xxs {
  height: 18px;

  .leadingIcon,
  .trailingIcon,
  .deleteButton {
    width: 12px;
    height: 12px;
  }
}

.xs {
  height: 20px;

  .leadingIcon,
  .trailingIcon,
  .deleteButton {
    width: 12px;
    height: 12px;
  }
}

.s {
  height: 24px;
  padding: 0 6px;
  border-radius: 8px;

  .leadingIcon,
  .trailingIcon,
  .deleteButton {
    width: 14px;
    height: 14px;
  }
}

.m {
  height: 28px;
  padding: 0 8px;
  border-radius: 8px;

  .content {
    gap: 8px;
  }

  .dot {
    width: 6px;
    height: 6px;
  }

  .leadingIcon,
  .trailingIcon,
  .deleteButton {
    width: 14px;
    height: 14px;
  }
}

// Colour variants
.blue {
  background-color: tag.$tag-blue-background;
  color: tag.$tag-blue-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-blue-hover;
  }
}

.blueStrong {
  background-color: tag.$tag-blue-strong-background;
  color: tag.$tag-blue-strong-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-blue-strong-hover;
  }
}

.blueSoft {
  background-color: tag.$tag-blue-soft-background;
  color: tag.$tag-blue-soft-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-blue-soft-hover;
  }
}

.green {
  background-color: tag.$tag-green-background;
  color: tag.$tag-green-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-green-hover;
  }
}

.orange {
  background-color: tag.$tag-orange-background;
  color: tag.$tag-orange-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-orange-hover;
  }
}

.amber {
  background-color: tag.$tag-amber-background;
  color: tag.$tag-amber-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-amber-hover;
  }
}

.purple {
  background-color: tag.$tag-purple-background;
  color: tag.$tag-purple-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-purple-hover;
  }
}

.cyan {
  background-color: tag.$tag-cyan-background;
  color: tag.$tag-cyan-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-cyan-hover;
  }
}

.crimson {
  background-color: tag.$tag-crimson-background;
  color: tag.$tag-crimson-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-crimson-hover;
  }
}

.red {
  background-color: tag.$tag-red-background;
  color: tag.$tag-red-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-red-hover;
  }
}

.yellow {
  background-color: tag.$tag-yellow-background;
  color: tag.$tag-yellow-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-yellow-hover;
  }
}

.grey {
  background-color: tag.$tag-grey-background;
  color: tag.$tag-grey-default;

  &:hover:not(.disabled) {
    background-color: tag.$tag-grey-hover;
  }
}

.clear {
  background-color: transparent;
  color: tag.$tag-grey-default;
  border: border.$border-width solid border.$border-muted;

  &:hover:not(.disabled) {
    background-color: surface.$surface-background;
  }
}

.white {
  background-color: surface.$surface-full;
  color: tag.$tag-grey-default;
  border: border.$border-width solid border.$border-muted;

  &:hover:not(.disabled) {
    background-color: surface.$surface-background;
  }
}

// Outline variant — overrides background of coloured variants and adds a hairline border.
// Order matters: this block must follow the coloured variants above so its selectors win.
.outline {
  border: border.$border-width solid transparent;

  &.blue {
    background-color: tag.$tag-blue-background-light;
    border-color: tag.$tag-blue-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-blue-background;
    }
  }

  &.green {
    background-color: tag.$tag-green-background-light;
    border-color: tag.$tag-green-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-green-background;
    }
  }

  &.orange {
    background-color: tag.$tag-orange-background-light;
    border-color: tag.$tag-orange-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-orange-background;
    }
  }

  &.purple {
    background-color: tag.$tag-purple-background-light;
    border-color: tag.$tag-purple-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-purple-background;
    }
  }

  &.cyan {
    background-color: tag.$tag-cyan-background-light;
    border-color: tag.$tag-cyan-hover;

    &:hover:not(.disabled) {
      background-color: tag.$tag-cyan-background;
    }
  }

  &.crimson {
    background-color: tag.$tag-crimson-background-light;
    border-color: tag.$tag-crimson-hover;

    &:hover:not(.disabled) {
      background-color: tag.$tag-crimson-background;
    }
  }

  &.red {
    background-color: tag.$tag-red-background-light;
    border-color: tag.$tag-red-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-red-background;
    }
  }

  &.yellow {
    background-color: tag.$tag-yellow-background-light;
    border-color: tag.$tag-yellow-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-yellow-background;
    }
  }

  &.grey {
    background-color: tag.$tag-grey-background-light;
    border-color: tag.$tag-grey-background;

    &:hover:not(.disabled) {
      background-color: tag.$tag-grey-background;
    }
  }
}

// Disabled state
.disabled {
  @include mixins.disabled;
}

// Inner elements
.content {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 2px;
}

.label {
  @include typography.text-s-medium;
  white-space: nowrap;
}

.dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: currentColor;
  flex-shrink: 0;
}

.leadingIcon,
.trailingIcon,
.deleteButton {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
}

.deleteButton {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: currentColor;
  opacity: 0.7;
  border-radius: 3px;
  transition: opacity 150ms ease;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }
}
